<template>
    <v-app-bar color="#f1f2f3" app flat height="66">
        <v-card flat color="transparent" class="w-100 d-flex mx-2 mt-2 align-center">
            <template v-if="showBreadcrumbs">
                <v-breadcrumbs :items="breadcrumbItems" class="appbar-breadcrumbs ml-2 flex-nowrap overflow-hidden">
                    <template v-slot:divider>
                        <v-icon>mdi-chevron-right</v-icon>
                    </template>
                    <template v-slot:item="{ item }">
                        <div v-if="item.selectable" class="breadcrumb-item d-flex flex-column overflow-hidden px-2">
                            <span class="caption text-uppercase text--disabled ml-4">{{ item.level }}</span>
                            <v-menu v-model="breadcrumbMenus[item.level]" offset-y :close-on-content-click="false">
                                <template v-slot:activator="{ on, attrs }">
                                    <v-btn
                                        text
                                        class="breadcrumb-button justify-start text-left text-none"
                                        :disabled="item.disabled"
                                        :title="item.text"
                                        v-bind="attrs"
                                        v-on="on">
                                        <span
                                            class="d-flex align-center text-body-1 overflow-hidden w-100"
                                            :class="{ 'font-weight-bold': item.isLastWithValue, 'text--disabled': item.muted }">
                                            <span class="d-block text-truncate flex-grow-1">{{ item.text }}</span>
                                            <v-icon small class="ml-1 flex-shrink-0">mdi-chevron-down</v-icon>
                                        </span>
                                    </v-btn>
                                </template>

                                <ContextSelectorList
                                    :level="item.level"
                                    :open="breadcrumbMenus[item.level]"
                                    :selected-id="selectedBreadcrumbId(item.level)"
                                    @select="selectBreadcrumbEntity(item.level, $event)"
                                    @view-all="viewAllBreadcrumbLevel(item.level)" />
                            </v-menu>
                        </div>
                        <div v-else class="breadcrumb-item d-flex flex-column overflow-hidden px-2" :title="item.text">
                            <span class="caption text-uppercase text--disabled">{{ item.level }}</span>
                            <span class="text-truncate" :class="{ 'font-weight-bold': item.isLastWithValue, 'text--disabled': item.muted }">
                                {{ item.text }}
                            </span>
                        </div>
                    </template>
                </v-breadcrumbs>
            </template>
            <v-spacer />

            <v-tooltip bottom v-if="displayAPIWarning">
                <template v-slot:activator="{ on, attrs }">
                    <v-btn v-bind="attrs" v-on="on">
                        <v-icon color="red">mdi-alert</v-icon>
                    </v-btn>
                </template>
                <span>You are connected to {{ customBaseURL }}</span>
            </v-tooltip>

            <div v-if="$auth.isAuthenticated" class="d-flex align-center justify-space-around flex-shrink-0">
                <template v-if="userInfo && userInfo.is_trial_user">
                    <v-chip label>
                        {{ trialText }}
                        <v-btn
                            v-if="$appConfig.VUE_APP_INTERCOM_ID"
                            elevation="0"
                            x-small
                            class="ml-5"
                            color="primary"
                            @click="showNewMessageIntercom(trialMsgIntercom)">
                            {{ trialButtonLabel }}
                        </v-btn>
                    </v-chip>
                </template>

                <LiveUpdatesIndicator />
                <ToolbarRunningApps />
                <UserNotificationsMenu />
            </div>
        </v-card>
    </v-app-bar>
</template>

<script>
import { mapGetters, mapState } from 'vuex'
import { enumsData } from '@/mixins/enums'
import { intercomMethods } from '@/mixins/intercom'
import { calculateDateDifferenceInDays } from '../utils'
import { isCurrentState, isDistributedInstance, isEditorRole, isViewerRole, sortArray } from '@/utils'
import ContextSelectorList from '@/components/contextSelectors/ContextSelectorList.vue'

const LiveUpdatesIndicator = () => import('@/components/LiveUpdatesIndicator.vue')
const ToolbarRunningApps = () => import('@/components/ToolbarRunningApps.vue')
const UserNotificationsMenu = () => import('@/components/UserNotificationsMenu.vue')

export default {
    name: 'AppBar',
    mixins: [enumsData, intercomMethods],
    components: {
        ContextSelectorList,
        LiveUpdatesIndicator,
        ToolbarRunningApps,
        UserNotificationsMenu
    },
    data() {
        return {
            customBaseURL: window.localStorage.getItem('nvBaseUrl'),
            breadcrumbMenus: {
                organization: false,
                space: false,
                instance: false,
                snapshot: false
            }
        }
    },
    methods: {
        closeBreadcrumbMenus() {
            Object.keys(this.breadcrumbMenus).forEach(level => {
                this.breadcrumbMenus[level] = false
            })
        },
        selectedBreadcrumbId(level) {
            return {
                organization: this.$route.params.oid,
                space: this.$route.params.sid,
                instance: this.activeInstanceId,
                snapshot: this.$route.params.snid
            }[level]
        },
        selectBreadcrumbEntity(level, entity) {
            if (level === 'organization') {
                this.openOrganization(entity)
            } else if (level === 'space') {
                this.openSpace(entity)
            } else if (level === 'instance') {
                this.openInstance(entity)
            } else if (level === 'snapshot') {
                this.openSnapshot(entity)
            }
        },
        viewAllBreadcrumbLevel(level) {
            if (level === 'space') this.goToSpaces()
            if (level === 'instance') this.goToInstances()
            if (level === 'snapshot') this.goToSnapshots()
        },
        openOrganization(org) {
            this.closeBreadcrumbMenus()
            if ((this.$route.name || '').startsWith('config')) {
                this.$router.push({ name: 'config-org-general', params: { oid: org.oid } })
                return
            }
            this.$router.push({ name: 'home-dashboard', params: { oid: org.oid } })
        },
        openSpace(space) {
            this.closeBreadcrumbMenus()
            if ((this.$route.name || '').startsWith('config')) {
                this.$router.push({
                    name: 'config-space-general',
                    params: { oid: this.$route.params.oid, sid: space.sid },
                    query: { ...this.$route.query }
                })
                return
            }
            this.$router.push({
                name: 'snapshot-overview',
                params: { oid: this.$route.params.oid, sid: space.sid, iid: space.iid, snid: space.snid }
            })
        },
        goToSpaces() {
            this.closeBreadcrumbMenus()
            this.$router.push({ name: 'home-spaces', params: { oid: this.$route.params.oid } })
        },
        goToInstances() {
            this.closeBreadcrumbMenus()
            const instance = this.spaceInstances.find(item => item.iid?.toString() === this.activeInstanceId?.toString()) || this.masterInstance
            if (!instance?.iid || !instance?.dev_snid) {
                return
            }
            this.$router.push({
                name: 'space-instances',
                params: {
                    oid: this.$route.params.oid,
                    sid: this.$route.params.sid,
                    iid: instance.iid,
                    snid: instance.dev_snid
                }
            })
        },
        goToSnapshots() {
            this.closeBreadcrumbMenus()
            this.$router.push({
                name: 'instance-snapshots',
                params: {
                    ...this.$route.params,
                    iid: this.activeInstanceId
                }
            })
        },
        redirectToConfigInstance(iid) {
            if (!(this.$route.name || '').startsWith('config')) {
                return false
            }
            this.$router.push({
                name: 'config-instance-general',
                params: { oid: this.$route.params.oid, sid: this.$route.params.sid, iid },
                query: { ...this.$route.query }
            })
            return true
        },
        showInstanceOpenSnackBar(instanceName) {
            this.$store.dispatch('showSnackBar', {
                snackBarText: `Moved to instance: ${instanceName}`,
                snackBarTimeout: 5000,
                snackBarIcon: 'check_circle'
            })
        },
        async openInstance(instance) {
            this.closeBreadcrumbMenus()
            if (this.redirectToConfigInstance(instance.iid)) {
                this.showInstanceOpenSnackBar(instance.long_id)
                return
            }

            const oid = this.$route.params.oid
            const sid = this.$route.params.sid
            if (isDistributedInstance(instance.long_id)) {
                this.openDistributedInstance(instance, oid, sid)
                return
            }

            await this.$store.dispatch('instanceStore/fetchSnapshots', instance.iid)
            if (this.$route.params.oid?.toString() !== oid?.toString() || this.$route.params.sid?.toString() !== sid?.toString()) {
                return
            }

            const snapshots = sortArray(this.instanceSnapshots, 'snid', isEditorRole(instance.role) ? 'ascending' : 'descending', false)
            if ((!isEditorRole(instance.role) && !isViewerRole(instance.role)) || !snapshots.length) {
                return
            }

            this.$router.push({
                name: 'snapshot-overview',
                params: { oid, sid, iid: instance.iid, snid: snapshots[0].snid }
            })
            this.showInstanceOpenSnackBar(instance.long_id)
        },
        async openDistributedInstance(instance, oid, sid) {
            try {
                const response = await this.$axios.get(`instances/${instance.iid}/snapshots`)
                if (this.$route.params.oid?.toString() !== oid?.toString() || this.$route.params.sid?.toString() !== sid?.toString()) {
                    return
                }
                const snapshots = sortArray(response.data || [], 'snid', 'descending', false).filter(snapshot => !isCurrentState(snapshot.short_id))
                if (snapshots.length) {
                    this.$router.push({
                        name: 'snapshot-overview',
                        params: { oid, sid, iid: instance.iid, snid: snapshots[0].snid }
                    })
                    this.showInstanceOpenSnackBar(instance.long_id)
                    return
                }
                this.$store.dispatch('showSnackBar', {
                    snackBarText: 'Distributed has no snapshots, resetting to master instance.',
                    snackBarTimeout: 5000,
                    snackBarIcon: 'info'
                })
                if (this.masterInstance) {
                    this.openInstance(this.masterInstance)
                }
            } catch (error) {
                console.log(error)
                this.$store.dispatch('showSnackBar', {
                    snackBarText: 'Failed to fetch snapshots of distributed',
                    snackBarTimeout: 5000,
                    snackBarIcon: 'error'
                })
            }
        },
        openSnapshot(snapshot) {
            this.closeBreadcrumbMenus()
            this.$router.push({
                name: 'snapshot-overview',
                params: {
                    oid: this.$route.params.oid,
                    sid: this.$route.params.sid,
                    iid: this.activeInstanceId,
                    snid: snapshot.snid
                }
            })
        }
    },
    computed: {
        ...mapState(['userInfo', 'trialInfo', 'userOrgs', 'currentOrgData']),
        ...mapState('userStore', ['availableResourcePools', 'selectedRP', 'selectedResourcePool']),
        ...mapState('spaceStore', ['masterInstance', 'spaceInstances']),
        ...mapState('instanceStore', ['instanceSnapshots']),
        ...mapGetters('orgStore', ['orgLongName']),
        ...mapGetters('spaceStore', ['currentSpaceName', 'isSpaceArchived', 'isSpaceRested']),
        ...mapGetters('instanceStore', ['instanceLongNameById']),
        ...mapGetters('snapshotStore', ['currentSnapshotName', 'snapshotLongNameById', 'isDevelopment']),
        selectedOrgName() {
            if (!this.$route.params.oid) {
                return null
            }
            if (this.orgLongName) {
                return this.orgLongName
            }
            const selectedOrg = this.userOrgs.find(org => org.oid.toString() === this.$route.params.oid.toString())
            if (selectedOrg?.long_id) {
                return selectedOrg.long_id
            }
            return this.currentOrgData?.long_id || `Organization ${this.$route.params.oid}`
        },
        isPoolSettingsRoute() {
            return (this.$route.name || '').startsWith('config-pool')
        },
        selectedResourcePoolName() {
            const routePoolId = Number(this.$route.params.rpid)
            const fallbackPoolId = Number(this.selectedResourcePool)
            const activePoolId = Number.isFinite(routePoolId) ? routePoolId : Number.isFinite(fallbackPoolId) ? fallbackPoolId : null

            if (this.selectedRP?.long_id && Number(this.selectedRP?.rpid) === activePoolId) {
                return this.selectedRP.long_id
            }

            const selectedPool = this.availableResourcePools.find(pool => Number(pool.rpid) === activePoolId)
            if (selectedPool?.long_id) {
                return selectedPool.long_id
            }

            if (activePoolId !== null) {
                return `Resource pool ${activePoolId}`
            }

            return null
        },
        selectedSpaceName() {
            if (!this.$route.params.sid) {
                return null
            }
            return this.currentSpaceName
        },
        selectedInstanceName() {
            if (!this.$route.params.sid) {
                return null
            }
            const isConfigRoute = (this.$route.name || '').startsWith('config')
            if (this.$route.params.iid) {
                return this.instanceLongNameById(this.$route.params.iid) || `Instance ${this.$route.params.iid}`
            }
            if (isConfigRoute) {
                return null
            }
            return this.masterInstance?.long_id || 'Master'
        },
        activeInstanceId() {
            return this.$route.params.iid || this.masterInstance?.iid || null
        },
        selectedSnapshotName() {
            if (this.isSpaceArchived && this.isDevelopment) {
                return 'Archived'
            }
            if (this.isSpaceRested && this.isDevelopment) {
                return 'Resting'
            }

            const snid = this.$route.params.snid
            if (!snid) {
                return null
            }

            const resolvedName = this.snapshotLongNameById(snid) || this.currentSnapshotName
            if (resolvedName) {
                return resolvedName
            }

            return `Snapshot ${snid}`
        },
        isInactiveSpace() {
            return Boolean(this.isSpaceArchived || this.isSpaceRested)
        },
        breadcrumbItems() {
            if (this.isPoolSettingsRoute) {
                return [
                    {
                        level: 'resource pool',
                        text: this.selectedResourcePoolName || '-',
                        disabled: true,
                        muted: false,
                        isLastWithValue: true,
                        selectable: false
                    }
                ]
            }

            const items = [
                { level: 'organization', value: this.selectedOrgName, disabled: !this.userOrgs.length, muted: false, selectable: true },
                {
                    level: 'space',
                    value: this.selectedSpaceName,
                    disabled: !this.$route.params.oid,
                    muted: this.isInactiveSpace,
                    selectable: true
                },
                {
                    level: 'instance',
                    value: this.selectedInstanceName,
                    disabled: !this.$route.params.sid,
                    muted: this.isInactiveSpace,
                    selectable: true
                },
                {
                    level: 'snapshot',
                    value: this.selectedSnapshotName,
                    disabled: !this.$route.params.sid || !this.activeInstanceId,
                    muted: this.isInactiveSpace,
                    selectable: true
                }
            ]

            const lastValueIndex = items.reduce((lastIndex, item, index) => {
                return item.value ? index : lastIndex
            }, -1)

            return items.map((item, index) => ({
                ...item,
                text: item.value || `Select ${item.level}`,
                isLastWithValue: index === lastValueIndex
            }))
        },
        trialText() {
            if (this.trialInfo && this.trialInfo.valid) {
                return `Trial period: ${this.trialDaysLeft} days left.`
            }
            if (this.trialInfo && !this.trialInfo.valid) {
                return `Your trial is up!`
            }
            return ''
        },
        trialButtonLabel() {
            if (this.trialInfo && this.trialInfo.tier === this.trialTiers.FREE) {
                return 'Upgrade'
            } else {
                return 'Convert'
            }
        },
        trialMsgIntercom() {
            if (this.trialInfo && this.trialInfo.tier === this.trialTiers.FREE) {
                return 'Hi Nuvolos Team,\n\nI would be interested in upgrading my trial account to test the complete Nuvolos experience.'
            } else {
                return 'Hi Nuvolos Team,\n\nI would like to convert my trial account to a regular plan account.'
            }
        },
        trialDaysLeft() {
            if (this.trialInfo && this.trialInfo.end_date) {
                return calculateDateDifferenceInDays(Date.now(), this.trialInfo.end_date)
            }
            return null
        },
        displayAPIWarning() {
            return this.$appConfig.VUE_APP_ENV !== 'production' && this.customBaseURL
        },
        showBreadcrumbs() {
            const routeName = this.$route.name || ''
            return (
                this.breadcrumbItems.length > 0 &&
                !routeName.includes('reporting') &&
                !['user-pools', 'user-pools-create-account', 'user-pools-create-wallet'].includes(routeName)
            )
        },
        showLogo() {
            return !this.$route.params.sid || (this.$route.name || '').includes('reporting')
        }
    }
}
</script>

<style scoped lang="scss">
.appbar-breadcrumbs {
    flex: 1 1 auto;
    min-width: 0;

    ::v-deep > li:not(.v-breadcrumbs__divider) {
        flex: 1 1 0;
        min-width: 0;
    }
}

.breadcrumb-item {
    min-width: 0;
    width: 100%;
}

.breadcrumb-button {
    max-width: 100%;

    ::v-deep .v-btn__content {
        min-width: 0;
        max-width: 100%;
    }
}
</style>
